import React from 'react';
import PropTypes from 'prop-types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Link } from 'react-router-dom';
import styled from 'styled-components';

import { colors } from 'utils/colors';

function ButtonFA({ className, children, icon, iconPosition, ...rest }) {
  return (
    <StyledButton className={className} {...rest}>
      {icon && (!iconPosition || iconPosition === 'left') && (
        <span className={`icon left ${className || ''}`}>
          <FontAwesomeIcon icon={icon} />
        </span>
      )}
      {children}
      {icon && iconPosition === 'right' && (
        <span className={`icon right ${className || ''}`}>
          <FontAwesomeIcon icon={icon} />
        </span>
      )}
    </StyledButton>
  );
}

ButtonFA.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.object,
    PropTypes.array,
  ]),
  icon: PropTypes.oneOfType([
    PropTypes.object,
    PropTypes.array,
    PropTypes.string,
  ]),
  iconPosition: PropTypes.oneOf(['left', 'right']),
  floated: PropTypes.string,
  color: PropTypes.string,
  className: PropTypes.string,
};

ButtonFA.defaultProps = {
  children: '',
  icon: null,
  iconPosition: 'left',
  floated: '',
  color: '',
  className: '',
};

export default ButtonFA;

const StyledButton = styled(
  ({ margin, color, floated, className, children, icon, ...rest }) =>
    rest.to ? (
      <Link className={className} {...rest}>
        {children}
      </Link>
    ) : (
      <button className={className} {...rest}>
        {children}
      </button>
    )
)`
  position: relative;
  border-radius: 4px;
  box-shadow: 0 0 0 1px rgba(34, 36, 38, 0.15) inset;
  font-size: 0.9rem;
  display: inline-block;
  min-height: 1em;
  min-width: 95px;
  height: 36px;
  border: none;
  text-align: center;
  outline: 0;
  transition: all 0.3s ease;
  overflow: hidden;
  color: rgba(0, 0, 0, 0.6);
  background-color: transparent;
  padding: ${(props) => (props.nopadding ? '0px;' : '0.65em 1em;')};
  vertical-align: top;
  float: ${(props) => props.floated || 'initial'};
  margin: ${(props) => props.margin || '0'};
  line-height: 1.4em !important;
  user-select: none !important;

  &:hover {
    box-shadow: inset 0 0 0 1.5px ${(props) => props.color} !important;
    color: rgba(0, 0, 0, 0.6);
  }

  &:focus {
    box-shadow: inset 0 0 0 1.5px ${(props) => props.color};
  }

  &:active {
    background-color: #f8f8f8;
    color: rgba(0, 0, 0, 0.9);
    box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.15) inset,
      0 1px 4px 0 rgba(34, 36, 38, 0.15) inset;
  }

  &.button-loading {
    color: transparent !important;

    &:after {
      content: '';
      position: absolute;
      top: 50%;
      left: 50%;
      margin: -0.6em 0 0 -0.6em;
      width: 16px;
      height: 16px;
      border-radius: 500rem;
      border: 0.2em solid rgba(0, 0, 0, 0.15);
      animation: spin 0.6s linear infinite;
      border-top-color: #767676;
    }

    .icon {
      display: none;
    }
  }

  &.loading {
    cursor: default;
    color: transparent !important;
    pointer-events: none !important;
    transition: none !important;
    box-shadow: 0 0 0 1px rgba(34, 36, 38, 0.15) inset;
    &:after {
      content: '';
      position: absolute;
      top: 50%;
      left: 50%;
      margin: -0.6em 0 0 -0.6em;
      width: 16px;
      height: 16px;
      border-radius: 500rem;
      border: 0.2em solid rgba(0, 0, 0, 0.15);
      animation: spin 0.6s linear infinite;
      border-top-color: #767676;
    }
    &:focus,
    &:active {
      color: transparent !important;
    }
    .icon {
      display: none;
    }
    @keyframes spin {
      0% {
        transform: rotate(0deg);
      }
      100% {
        transform: rotate(360deg);
      }
    }
  }

  &.disabled {
    cursor: default;
    pointer-events: none !important;
    opacity: 0.45;
  }

  &.basic {
    font-size: 1rem;
    padding: 0.78571429em 1.5em;
    line-height: 1rem !important;

    &:hover,
    &:focus {
      box-shadow: 0 0 0 1px
        ${(props) => props.color || 'rgba(34, 36, 38, 0.35)'} inset !important;
      color: ${(props) => props.color || 'rgba(0,0,0,0.6)'};
    }
  }

  &.inverted {
    box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.5) inset !important;
    color: #fff;
    font-size: 1rem;
    line-height: 1.3rem !important;

    &:hover,
    &:focus {
      box-shadow: 0 0 0 2px ${(props) => props.color} inset !important;
      color: ${(props) => props.color};
      background-color: transparent;
    }
  }

  &.negative {
    && {
      box-shadow: 0 0 0 1px #db2828 inset !important;
      color: #db2828 !important;
    }
  }

  &.left {
    padding: 0.65em 1.5em 0.65em 4em;

    &:focus {
      box-shadow: 0 0 0 1px rgb(34 36 38 / 15%) inset !important;
    }

    &:hover {
      box-shadow: 0 0 0 2px ${(props) => props.color} inset !important;
    }
  }

  &.grey-focus {
    &:focus {
      color: #fff;
      box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.5) inset !important;
    }
  }

  &.grey-focus2 {
    &:focus {
      box-shadow: 0 0 0 2px rgba(34, 36, 38, 0.15) inset !important;
    }
  }

  &.right {
    padding: 0.65em 4em 0.65em 1.5em;
  }

  &.centered {
    display: block;
    margin: 1rem auto 0 auto;
  }

  &.fluid {
    width: 100%;
  }

  .icon {
    margin: 0;
    height: 100%;
    position: absolute;
    color: ${(props) => determineIconColor(props.color)};
    top: 0;
    width: 36px;
    background-color: ${(props) => props.color};

    display: flex;
    align-items: center;
    justify-content: center;

    // Fix for FontAwesome SVG vertical alignment
    svg {
      font-size: 1;
      vertical-align: middle;
      display: block;
    }

    &.left {
      left: 0;
      right: initial;
    }

    &.right {
      right: 0;
      left: initial;
    }

    img {
      height: 17px;
      width: 17px;
      transform: rotate(43deg);
      margin: 10px;
    }
  }
`;

function determineIconColor(color) {
  switch (color) {
    case colors.darkBlue:
    case colors.red:
    case colors.blue:
    case colors.darkGrey:
    case colors.green:
    case colors.purple:
    case colors.pink:
    case colors.navyBlue:
    case colors.darkTeal:
    case colors.buttonGrey:
      return 'white';
    default:
      return 'rgba(0,0,0,0.6)';
  }
}
